import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useRef, useState } from "react";
import BasicChart from "../charts/BasicChart";
import { configAreaChartData } from "../charts/configAreaChartData";
import { configBarChartData } from "../charts/configBarChartData";
import { configBoxPlotData } from "../charts/configBoxPlotData";
import { configHistogramData } from "../charts/configHistogramData";
import { configLineChartData } from "../charts/configLineChartData";
import { configPieChartData } from "../charts/configPieChartData";
import { configScatterChartData } from "../charts/configScatterChart";
import RawDatasetsTable from "../charts/RawDatasetsTable";
import Stats from "../charts/Stats";
import { convertDataToChartsData, getAllDatasetsTimestamps, mapDataTable } from "../charts/utils";
import ChartTypeMenu from "./ChartTypeMenu";
import DateTimeRangeSelection from "./DateTimeRangeSelection";
import DragArea from "./DragArea";
import { getData } from "./getData";
import { IDataSet } from "./interfaces";
import SettingsDialog from "./SettingsDialog";
import { AggregationType, ChartData, ChartType, Dataset } from "./types";
import { createIStatsRows, createPieData, isDate2BeforeDate1, minusSixHoursDate } from "./utils";
import "./style.css";
import { useResizeDetector } from "react-resize-detector";
import { configMultiScatterChartData } from "../charts/configMultiScatterChart";
import MultiDragArea from "./DragArea/MultiDragArea";
import DatasetInfoByID from "../dataset-info-by-id";

const DatasetView = ({ selectedDatasetsID, removeDataset }: { selectedDatasetsID: Array<Dataset>; removeDataset: any }) => {
  const today: Date = new Date();
  const sixHoursBeforeToday: Date = minusSixHoursDate(today);
  const [dateFrom, setDateFrom] = useState<Date>(sixHoursBeforeToday);
  const [dateTo, setDateTo] = useState<Date>(today);
  const [invalidRange, setInvalidRange] = useState<boolean>(false);
  const [data, setData] = useState<IDataSet[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [calculated, setCalculated] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState<ChartType>("LINE");
  const [dataPointCount, setDataPointCount] = useState<number>(0);
  const [displaySettings, setDisplaySettings] = useState<boolean>(false);
  const [enableSettings, setEnableSettings] = useState<boolean>(false);
  const [showAxisSelection, setShowAxisSelection] = useState<boolean>(false);
  const [enableMultiAxis, setEnableMultiAxkis] = useState<boolean>(false);
  const [selectedColorPalette, setSelectedColorPalette] = useState<{ key: string; value: Array<string> }>({
    key: "Category10",
    value: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"],
  });
  const [xAxisID, setXAxisID] = useState<string>("");
  const [yAxisIDS, setYAxisIDS] = useState<Array<string>>([]);
  const [x1AxisID, setX1AxisID] = useState<string>("");
  const [y1AxisID, setY1AxisID] = useState<string>("");
  const [colorAxisID, setColorAxisID] = useState<string>("");
  const [sizeAxisID, setSizeAxisID] = useState<string>("");
  const [aggregationMethod, setAggregationMethod] = useState<AggregationType>("SUM");
  const [dataCacheMap, setDataCacheMap] = useState<Map<ChartType, any>>(new Map());
  const [calculatedChartMap, setCalculatedChartMap] = useState<Map<ChartType, boolean | undefined>>(new Map());
  const updateDataCacheMap = (k: ChartType, v: any, isCalculated?: boolean) => {
    setDataCacheMap(new Map(dataCacheMap.set(k, v)));
    setCalculatedChartMap(new Map(calculatedChartMap.set(k, isCalculated)));
  };
  const updateCalculatedChartMap = (k: ChartType, v: boolean) => {
    setCalculatedChartMap(new Map(calculatedChartMap.set(k, v)));
  };

  const clearDataCacheMap = () => {
    setDataCacheMap(new Map());
    setCalculatedChartMap(new Map());
  };

  useEffect(() => {
    if (isDate2BeforeDate1(dateFrom, dateTo)) {
      setInvalidRange(true);
    } else {
      setDataPointCount(0);
      setInvalidRange(false);
    }
  }, [dateFrom, dateTo]);

  /*   const [dataCacheMap, setDataCacheMap] = new Map<String, any>()

  function getChartData(chartDataType: any, response: any) {
    if map constains chartDataType:
      return dataCacheMap[chartDataType]
    else:
      dataCacheMap[chartDataType] = mapData()
  }
  clear map if data changes
 */

  const mapDataCache = () => {
    const convertedData = convertDataToChartsData(data);
    updateDataCacheMap("BASIC", convertedData, true);
    switch (selectedChartType) {
      /* case "LINE": {
        updateCalculatedChartMap("LINE", true);
        break;
      }
      case "BAR": {
        updateCalculatedChartMap("BAR", true);
        break;
      }
      case "BASIC": {
        updateCalculatedChartMap("BASIC", true);
        break;
      }
      case "BOX": {
        updateCalculatedChartMap("BOX", true);
        break;
      }
      case "HISTOGRAM": {
        updateCalculatedChartMap("HISTOGRAM", true);
        break;
      } */
      case "PIE": {
        updateCalculatedChartMap("PIE", false);
        updateDataCacheMap(selectedChartType, createPieData(data, aggregationMethod));
        updateCalculatedChartMap("PIE", true);
        break;
      }
      case "STATS": {
        updateCalculatedChartMap("STATS", false);
        updateDataCacheMap(selectedChartType, createIStatsRows(data));
        updateCalculatedChartMap("STATS", true);
        break;
      }
      case "RAW": {
        updateCalculatedChartMap("RAW", false);
        setLoading(true);
        setLoaded(false);
        updateDataCacheMap(selectedChartType, getAllDatasetsTimestamps(data));
        //mapDataTable(data)
        setLoading(false);
        updateCalculatedChartMap("RAW", true);
        setLoaded(true);
        break;
      }
      case "SCATTER": {
        //setCalculated(true);
        break;
      }
      case "MULTISCATTER": {
        // setCalculated(true);
        //==setCalculated(false);
        break;
      }
      default: {
        break;
      }
    }
  };

  const updateCalculatedBasicState = (state: boolean) => {
    updateCalculatedChartMap("BASIC", state);
    updateCalculatedChartMap("LINE", state);
    updateCalculatedChartMap("BAR", state);
    updateCalculatedChartMap("BOX", state);
    updateCalculatedChartMap("HISTOGRAM", state);
  }

  const createChartData = (dataHasChanged: boolean) => {
    updateCalculatedBasicState(false)
    let _selectedChartType: ChartType;
    if (selectedChartType === "LINE" || selectedChartType === "BAR" || selectedChartType === "BOX" || selectedChartType === "HISTOGRAM") {
      _selectedChartType = "BASIC";
    } else {
      _selectedChartType = selectedChartType;
    }
    //mapDataCache();
    if (dataHasChanged || !dataCacheMap.has(_selectedChartType)) {
      mapDataCache();
    } else {
      updateCalculatedBasicState(true)
    }
  };


  useEffect(() => {
    createChartData(false);
  }, [selectedChartType]);
  useEffect(() => {
    createChartData(true);
  }, [data]);

  const selectDateFrom = (dateFrom: Date) => {
    setDateFrom(dateFrom);
  };

  const selectDateTo = (dateTo: Date) => {
    setDateTo(dateTo);
  };

  useEffect(() => {
    const asyncGetData = async (datasetsIDs: Array<string>) => {
      setLoading(true);
      const result: Array<IDataSet> = await getData(dateFrom, dateTo, datasetsIDs).then(async (res: any) => {
        if (res !== undefined) {
          clearDataCacheMap();
          setData(res);
          const countedPoints: number = res.reduce(
            (acc: number, current: any) => {
              acc = dataPointCount + current.data.length;
              return acc;
            },
            [dataPointCount]
          );
          setDataPointCount(countedPoints);
          setLoading(false);
          setLoaded(true);
        }
        return result;
      });
    };
    if (selectedDatasetsID.length > 0) {
      const datasetsIDs = selectedDatasetsID.map((s) => s.identifier);
      asyncGetData(datasetsIDs);
    } else {
      setLoaded(false);
      setEnableSettings(false);
    }
  }, [dateFrom, dateTo, selectedDatasetsID]);

  useEffect(() => {
    if (selectedDatasetsID.length > 0 && selectChartType !== undefined && loaded === true) {
      setEnableSettings(true);
    }
  }, [selectedChartType, selectedDatasetsID]);

  useEffect(() => {
    updateCalculatedChartMap("PIE", false);
    if (data.length > 0) {
      updateDataCacheMap("PIE", createPieData(data, aggregationMethod), true);
    }
  }, [aggregationMethod]);

  const selectChartType = (chartType: ChartType) => {
    setSelectedChartType(chartType);
    if (chartType === "SCATTER") {
      setShowAxisSelection(true);
    } else {
      setShowAxisSelection(false);
    }
    if (chartType === "MULTISCATTER") {
      setEnableMultiAxkis(true);
    } else {
      setEnableMultiAxkis(false);
    }
  };

  const selectAggregationMethod = (aggrMethod: AggregationType) => {
    setAggregationMethod(aggrMethod);
  };

  const handleSettings = () => {
    setDisplaySettings(true);
  };

  const onHide = () => {
    setDisplaySettings(false);
  };

  const selectColorPalette = (colorsArr: { key: string; value: Array<any> }) => {
    setSelectedColorPalette(colorsArr);
  };

  const assignToAxis = (xAxisId: string, yAxisIds: Array<string>) => {
    setXAxisID(xAxisId);
    setYAxisIDS(yAxisIds);
  };

  const assignToMultiAxis = (x1AxisId: string, y1AxisId: string, colorAxisId: string, sizeAxisId: string) => {
    setX1AxisID(x1AxisId);
    setY1AxisID(y1AxisId);
    setColorAxisID(colorAxisId);
    setSizeAxisID(sizeAxisId);
  };

  useEffect(() => {
    if (selectedChartType === "SCATTER") {
      updateCalculatedChartMap("SCATTER", false);
      if (xAxisID !== undefined && yAxisIDS !== undefined && dataCacheMap.get("BASIC") !== undefined) {
        const datasetXAxis: ChartData | undefined = dataCacheMap.get("BASIC").find((s: any) => s.id === xAxisID);
        const datasetsYAxis: Array<ChartData | undefined> = yAxisIDS.map((yAxisID) => {
          return dataCacheMap.get("BASIC").find((s: any) => {
            return s.id === yAxisID;
          });
        });
        if (datasetXAxis !== undefined && datasetsYAxis !== undefined) {
          const scatterData: { X: ChartData; Y: Array<ChartData> } = { X: datasetXAxis, Y: datasetsYAxis as Array<ChartData> };
          updateDataCacheMap("SCATTER", scatterData, true);
        }
      }
    }
  }, [xAxisID, yAxisIDS, data, selectedChartType]);

  useEffect(() => {
    if (selectedChartType === "MULTISCATTER") {
      updateCalculatedChartMap("MULTISCATTER", false);
      if (dataCacheMap.get("BASIC") !== undefined) {
        const datasetXAxis: ChartData | undefined = dataCacheMap.get("BASIC").find((s: any) => s.id === x1AxisID);
        const datasetYAxis: ChartData | undefined = dataCacheMap.get("BASIC").find((s: any) => s.id === y1AxisID);
        const datasetColorAxis: ChartData | undefined = dataCacheMap.get("BASIC").find((s: any) => s.id === colorAxisID);
        const datasetSizeAxis: ChartData | undefined = dataCacheMap.get("BASIC").find((s: any) => s.id === sizeAxisID);

        const scatterData: { X?: ChartData; Y?: ChartData; color?: ChartData; size?: ChartData } = {
          X: datasetXAxis,
          Y: datasetYAxis,
          color: datasetColorAxis,
          size: datasetSizeAxis,
        };
        if (scatterData.X !== undefined && scatterData.Y !== undefined && scatterData.color !== undefined && scatterData.size !== undefined) {
          updateDataCacheMap("MULTISCATTER", scatterData, true);
        }
      }
    }
  }, [x1AxisID, y1AxisID, colorAxisID, sizeAxisID, data, selectedChartType]);

  const { width, height, ref } = useResizeDetector();
  /*   useEffect(() => {
    console.log(ref, width, height);
  }, [ref, width, height]);  */


  return (
    <div className="datasetView dynamicRow fullWidth">
      {displaySettings && selectedChartType && (
        <SettingsDialog
          displayDialog={displaySettings}
          selectedChartType={selectedChartType}
          onHide={onHide}
          data={data}
          selectColorPalette={selectColorPalette}
          selectedColorPalette={selectedColorPalette}
          selectAggregationMethod={selectAggregationMethod}
          aggregationMethod={aggregationMethod}
        />
      )}
      <div className="chartMenu cardSlim">
        <ChartTypeMenu loaded={loaded} selectChartType={selectChartType} />
      </div>
      <div className="flexColumn fullWidth">
        <div className="optionsFields cardSlim" ref={ref}>
          <div className="dateTimeSelection center">
            <DateTimeRangeSelection
              selectDateFrom={selectDateFrom}
              selectDateTo={selectDateTo}
              dateFrom={dateFrom}
              dateTo={dateTo}
              invalidRange={invalidRange}
            />
          </div>
          <div className="side flexRow center">
            {loaded && (
              <div className="flexColumn center">
                <h3>{selectedChartType === "RAW" ? "DATA TABLE" : selectedChartType}</h3>
                <p>Loaded data points: {dataPointCount}</p>
              </div>
            )}
            <DatasetInfoByID />
            <Button
              icon="pi pi-cog"
              iconPos="left"
              onClick={handleSettings}
              className="p-button-rounded p-button-secondary p-button-text center"
              disabled={!enableSettings}
            ></Button>
          </div>
        </div>
        <div className="dataView" style={{ width: width }}>
          {loading && (
            <div className="flex align-items-center justify-content-center">
              <ProgressSpinner />
            </div>
          )}
          {loaded && !!calculatedChartMap.get("BASIC") && selectedChartType === "LINE" && (
            <BasicChart plotData={configLineChartData(dataCacheMap.get("BASIC"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("BOX") && selectedChartType === "BOX" && (
            <BasicChart plotData={configBoxPlotData(dataCacheMap.get("BASIC"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("BAR") && selectedChartType === "BAR" && dataPointCount < 350 && (
            <BasicChart plotData={configBarChartData(dataCacheMap.get("BASIC"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("BAR") && selectedChartType === "BAR" && dataPointCount > 350 && (
            <BasicChart plotData={configAreaChartData(dataCacheMap.get("BASIC"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("HISTOGRAM") && selectedChartType === "HISTOGRAM" && (
            <BasicChart plotData={configHistogramData(dataCacheMap.get("BASIC"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("PIE") && selectedChartType === "PIE" && (
            <BasicChart plotData={configPieChartData(dataCacheMap.get("PIE"), selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("STATS") && selectedChartType === "STATS" && <Stats statsArr={dataCacheMap.get("STATS")} />}
          {loaded && !!calculatedChartMap.get("RAW") && selectedChartType === "RAW" && <RawDatasetsTable dataTable={dataCacheMap.get("RAW")} />}
          {loaded && !!calculatedChartMap.get("SCATTER") && selectedChartType === "SCATTER" && (
            <BasicChart plotData={configScatterChartData(dataCacheMap.get("SCATTER").X, dataCacheMap.get("SCATTER").Y, selectedColorPalette.value)} />
          )}
          {loaded && !!calculatedChartMap.get("MULTISCATTER") && selectedChartType === "MULTISCATTER" && (
            <BasicChart
              plotData={configMultiScatterChartData(
                dataCacheMap.get("MULTISCATTER").X,
                dataCacheMap.get("MULTISCATTER").Y,
                dataCacheMap.get("MULTISCATTER").color,
                dataCacheMap.get("MULTISCATTER").size,
                selectedColorPalette.value
              )}
            />
          )}
        </div>
        <div className="bottom cardSlim" style={{ width: width }}>
          {!enableMultiAxis && (
            <DragArea
              selectedDatasetsArr={selectedDatasetsID}
              removeDataset={removeDataset}
              enableXAxis={true}
              showAxisSelection={showAxisSelection}
              assignToAxis={assignToAxis}
            />
          )}
          {enableMultiAxis && (
            <MultiDragArea
              selectedDatasetsArr={selectedDatasetsID}
              removeDataset={removeDataset}
              enableXAxis={true}
              showAxisSelection={showAxisSelection}
              assignToMultiAxis={assignToMultiAxis}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DatasetView;
